import type { ReactNode } from "react";
import { Link } from "react-router";
import styles from "./Button.module.css";

type Variant = "primary" | "ghost";

interface CommonProps {
  children: ReactNode;
  variant?: Variant;
  arrow?: "right" | "down" | "external" | false;
  className?: string;
}

type ButtonProps = CommonProps &
  ({ to: string; href?: never } | { href: string; to?: never } | { to?: never; href?: never; type?: "button" | "submit"; disabled?: boolean; onClick?: () => void });

const ARROWS = { right: "→", down: "↓", external: "↗" } as const;

export function Button({ children, variant = "primary", arrow = "right", className, ...rest }: ButtonProps) {
  const cls = [styles.button, styles[variant], className].filter(Boolean).join(" ");
  const inner = (
    <>
      <span className={styles.label}>{children}</span>
      {arrow && (
        <span className={styles.arrow} aria-hidden="true">
          {ARROWS[arrow]}
        </span>
      )}
    </>
  );
  if ("to" in rest && rest.to) {
    return (
      <Link to={rest.to} className={cls}>
        {inner}
      </Link>
    );
  }
  if ("href" in rest && rest.href) {
    const external = /^https?:/.test(rest.href);
    return (
      <a href={rest.href} className={cls} {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}>
        {inner}
      </a>
    );
  }
  const { type = "button", disabled, onClick } = rest as {
    type?: "button" | "submit";
    disabled?: boolean;
    onClick?: () => void;
  };
  return (
    <button type={type} disabled={disabled} onClick={onClick} className={cls}>
      {inner}
    </button>
  );
}
